import React, { useContext, useState } from 'react'
import { Button } from '@/components/ui/button'
import { LabeledValue } from 'antd/es/select'
import { ClientNationalityDto } from 'api/Clients/Types/ClientDetailedDto'
import { CreateNciHistory } from 'api/Clients/Types/CreateNciHistory'
import { NciPriorityDto } from 'api/Clients/Types/NciPriorityDto'
import { DocumentTypeDto } from 'api/Documents/Types/DocumentTypeDto'
import {
  FormBaseAntSelectWrapper,
  FormBaseButtonContainer,
  FormBaseContainer,
  FormBaseErrorMessage,
  FormBaseFieldBlock,
  FormBaseFieldGroup,
  FormBaseFieldLabel,
  FormBaseRow,
} from 'components/Form/Form.styles'
import { Option, Select } from 'components/Select'
import { successToast, ToastContext } from 'components/Toast/Toast'
import { Field, Form, FormikErrors, FormikProps, withFormik } from 'formik'
import omitEmpty from 'omit-empty'
import { FormSubmitValues } from 'utils/formValidation'
import { truncateString } from 'utils/string.utils'

import { DocumentDto } from '../../../../../api/Documents/Types/Document'
import { useApiClient } from '../../../../../utils/api/ApiClient'
import { ClientsApiClient } from '../../../../../utils/api/clientsApi'
import { DocumentApiClient } from '../../../../../utils/api/documentApi'

type AddNCIHistoryRecord = Omit<CreateNciHistory, 'documentIds'> & { relations: LabeledValue[] }

const ClientDetailsNCIAddFormUI: React.FC<FormikProps<AddNCIHistoryRecord> & OuterProps> = (
  props
) => {
  const { handleSubmit, setSubmitting, isSubmitting, errors, setFieldValue, values } = props

  const handleSelectPriority = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = e.target
    const country = props.nciPriorities.find((prior) => prior.id === value)
    if (country) {
      setFieldValue('nciPriorityId', value)
      props.getNCIDocumentType(value)
    }
  }
  const handleSelectDocumentTypeId = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = e.target
    setFieldValue('documentTypeId', value)
    props.getDocumentsBasedOnType(value)
  }

  const handleSelectCountry = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = e.target
    const country = props.countries.find((prior) => prior.id === value)
    if (country) {
      setFieldValue('countryId', country.id)
      props.getPriorities(value)
    }
  }

  const onSubmit = () => {
    setSubmitting(true)
    handleSubmit()
  }

  return (
    <FormBaseContainer>
      <Form>
        <FormBaseFieldBlock>
          <FormBaseRow>
            <FormBaseFieldLabel>Country</FormBaseFieldLabel>
            <FormBaseFieldGroup>
              <Field component='select' name='countryId' onChange={handleSelectCountry}>
                <option value='' disabled>
                  None
                </option>
                {props.countries.map((country) => (
                  <option key={country.id} value={country.id}>
                    {country.name}
                  </option>
                ))}
              </Field>
            </FormBaseFieldGroup>
          </FormBaseRow>
          {errors.countryId && <FormBaseErrorMessage>{errors.countryId}</FormBaseErrorMessage>}
        </FormBaseFieldBlock>
        <FormBaseFieldBlock>
          <FormBaseRow>
            <FormBaseFieldLabel>Type</FormBaseFieldLabel>
            <FormBaseFieldGroup>
              <Field component='select' name='nciPriorityId' onChange={handleSelectPriority}>
                <option value='' disabled>
                  None
                </option>
                {props.nciPriorities.map((nci) => (
                  <option key={nci.id} value={nci.id}>
                    {nci.nciRule.name}
                  </option>
                ))}
              </Field>
            </FormBaseFieldGroup>
          </FormBaseRow>
          {errors.nciPriorityId && (
            <FormBaseErrorMessage>{errors.nciPriorityId}</FormBaseErrorMessage>
          )}
        </FormBaseFieldBlock>
        <FormBaseFieldBlock>
          <FormBaseRow>
            <FormBaseFieldLabel>Document</FormBaseFieldLabel>
            <FormBaseFieldGroup>
              <Field component='select' name='documentTypeId' onChange={handleSelectDocumentTypeId}>
                <option value='' disabled>
                  None
                </option>
                {props.documentType.map((docType) => (
                  <option key={docType.documentTypeId} value={docType.documentTypeId}>
                    {docType.name}
                  </option>
                ))}
              </Field>
            </FormBaseFieldGroup>
          </FormBaseRow>
          {errors.documentTypeId && (
            <FormBaseErrorMessage>{errors.documentTypeId}</FormBaseErrorMessage>
          )}
        </FormBaseFieldBlock>
        <FormBaseFieldBlock>
          <FormBaseRow>
            <FormBaseFieldLabel>Document Number</FormBaseFieldLabel>
            <FormBaseFieldGroup>
              <Field type='text' name='nciDocumentNumber' />
            </FormBaseFieldGroup>
          </FormBaseRow>
          {errors.nciDocumentNumber && (
            <FormBaseErrorMessage>{errors.nciDocumentNumber}</FormBaseErrorMessage>
          )}
        </FormBaseFieldBlock>
        <FormBaseFieldBlock>
          <FormBaseRow>
            <FormBaseFieldLabel>Relation</FormBaseFieldLabel>
            <FormBaseAntSelectWrapper>
              <FormBaseFieldGroup>
                <Select
                  labelInValue={true}
                  placeholder='Related Document'
                  value={values.relations}
                  allowClear={true}
                  showSearch
                  onChange={(value: LabeledValue) => {
                    if (!value) {
                      return setFieldValue('relations', [])
                    }
                    setFieldValue('relations', [{ value: value.value }])
                  }}
                >
                  {(props.documents || []).map((document) => (
                    <Option value={document.id} key={document.id}>
                      {truncateString(document.name, 100)}
                    </Option>
                  ))}
                </Select>
              </FormBaseFieldGroup>
            </FormBaseAntSelectWrapper>
          </FormBaseRow>
        </FormBaseFieldBlock>
        <FormBaseButtonContainer>
          <Button disabled={isSubmitting} key='button' type='button' onClick={onSubmit}>
            Submit
          </Button>
        </FormBaseButtonContainer>
      </Form>
    </FormBaseContainer>
  )
}

function dataToSubmit(values: AddNCIHistoryRecord): FormSubmitValues<CreateNciHistory> {
  return {
    clientId: values.clientId,
    countryId: values.countryId,
    documentTypeId: values.documentTypeId,
    nciDocumentNumber: values.nciDocumentNumber,
    nciPriorityId: values.nciPriorityId,
    teamIds: values.teamIds,
    statusId: '2',
    documentIds: values.relations.map((x) => x.value as string),
  }
}

interface OuterProps {
  clientId: string
  documentType: DocumentTypeDto[]
  nciPriorities: NciPriorityDto[]
  countries: ClientNationalityDto[]
  documents: { name: string; id: string }[]

  getPriorities(countryId: string): void
  getNCIDocumentType(priorityId: string): void
  getDocumentsBasedOnType(typeId: string): void
  onSubmit(values: FormSubmitValues<CreateNciHistory>): Promise<void>
}

export const ClientDetailsNCIAddForm = withFormik<OuterProps, AddNCIHistoryRecord>({
  mapPropsToValues: ({ clientId }) => {
    return {
      clientId,
      nciPriorityId: '',
      nciDocumentNumber: '',
      documentTypeId: '',
      statusId: '',
      teamIds: '',
      countryId: '',
      relations: [],
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit(omitEmpty(dataToSubmit(values)))
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values) => {
    const errors: FormikErrors<AddNCIHistoryRecord> = {}
    if (!values.documentTypeId) {
      errors.documentTypeId = 'required'
    }
    if (!values.nciDocumentNumber) {
      errors.nciDocumentNumber = 'required'
    }
    if (!values.nciPriorityId) {
      errors.nciPriorityId = 'required'
    }
    return errors
  },
  enableReinitialize: true,
})(ClientDetailsNCIAddFormUI)

interface Props {
  clientId: string
  tickmillCompanyId: number
  countries: ClientNationalityDto[]

  closeModal(): void
}

const ClientDetailsNCIAdd: React.FC<Props> = ({
  clientId,
  tickmillCompanyId,
  closeModal,
  countries,
}) => {
  const documentApiClient = useApiClient(DocumentApiClient)
  const clientsApiClient = useApiClient(ClientsApiClient)

  const [documentType, setDocumentType] = useState<DocumentTypeDto[]>([])
  const [nciPriorities, setNciPriorities] = useState<NciPriorityDto[]>([])
  const [documents, setDocuments] = useState<DocumentDto[]>([])
  const setToast = useContext(ToastContext)
  const getNCIDocumentType = async (priorityId: string) => {
    const documentTypes = await clientsApiClient.getClientNCIDocument(priorityId)
    setDocumentType(documentTypes)
  }

  const getDocumentsBasedOnType = async (typeID: string) => {
    const response = await documentApiClient.getDocumentsByClientAndTypeId(clientId, typeID)
    setDocuments(response.items)
  }

  const getPriorities = async (countryId: string) => {
    const response = await clientsApiClient.getClientNciPriorities(countryId, tickmillCompanyId)
    setNciPriorities(response)
  }

  const onSubmit = async (values: CreateNciHistory) => {
    await clientsApiClient.addClientNciHistoryRecords(values)
    setToast(successToast('NCI added.'))
    closeModal()
  }

  return (
    <ClientDetailsNCIAddForm
      countries={countries}
      nciPriorities={nciPriorities}
      getNCIDocumentType={getNCIDocumentType}
      getPriorities={getPriorities}
      getDocumentsBasedOnType={getDocumentsBasedOnType}
      documentType={documentType}
      clientId={clientId}
      documents={(documents || []).map((x) => ({ id: x.id, name: x.originalFilename }))}
      onSubmit={onSubmit}
    />
  )
}

export default ClientDetailsNCIAdd
